import type { ReactNode } from "react";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { createStore, useStore } from "zustand";

export type DisplayMode = "interactive" | "select";

export type ModifiableElementType =
  | "text"
  | "container"
  | "image"
  | "video"
  | "product-section"
  | "button"
  | "unknown";

type StyleOverrides = {
  fontSize: number | null;
  fontWeight: number | null;
  lineHeight: number | null;
  fontFamily: string | null;
  letterSpacing: number | null;
  color: string | null;
  colorHex: string | null;
  textAlign: string | null;
  fontStyle: string | null;
  textTransform: string | null;
  textDecoration: string | null;
  textDecorationLine: string | null;
  marginTop: number | null;
  marginRight: number | null;
  marginBottom: number | null;
  marginLeft: number | null;
  paddingTop: number | null;
  paddingRight: number | null;
  paddingBottom: number | null;
  paddingLeft: number | null;
  gap: number | null;
  flexDirection: string | null;
  justifyContent: string | null;
  alignItems: string | null;
  display: string | null;
  boxShadow: string | null;
  width: number | "auto" | "full" | null;
  height: number | "auto" | "full" | null;
  backgroundColor: string | null;
  backgroundColorHex: string | null;
  borderColor: string | null;
  borderColorHex: string | null;
  borderTopStyle: string | null;
  borderRightStyle: string | null;
  borderBottomStyle: string | null;
  borderLeftStyle: string | null;
  borderTopWidth: number | null;
  borderRightWidth: number | null;
  borderBottomWidth: number | null;
  borderLeftWidth: number | null;
  cornerTopLeftRadius: number | null;
  cornerTopRightRadius: number | null;
  cornerBottomRightRadius: number | null;
  cornerBottomLeftRadius: number | null;
  opacity: number | null;
};

type SelectedElement = {
  props: { [x: string]: any };
  file: string | null;
  line: number | null;
  column: number | null;
  childText: string | null;
  elementType: ModifiableElementType;
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  tagName: string;
  styleOverrides: Partial<StyleOverrides>;
};

interface ModifiersStoreData {
  selectedElement: SelectedElement | null;
  editText: string | null;
  classNames: string;
  displayMode: DisplayMode;
  contextMenuOpen: boolean;
  actions: {
    setSelectedElement: (selectedElement: SelectedElement | null) => void;
    setEditText: (editText: string | null) => void;
    setClassNames: (classNames: string) => void;
    setDisplayMode: (displayMode: DisplayMode) => void;
    setContextMenuOpen: (contextMenuOpen: boolean) => void;
    toggleDisplayMode: () => void;
    resetToDefaults: () => void;
  };
}

const createModifiersStore = () => {
  return createStore<ModifiersStoreData>((set, get) => ({
    selectedElement: null,
    editText: null,
    classNames: "",
    displayMode: "interactive",
    sectionClicked: false,
    contextMenuOpen: false,
    actions: {
      setSelectedElement: (selectedElement: SelectedElement | null) => {
        // When setting a selected element, also derive the related values
        if (selectedElement) {
          set({
            selectedElement,
            editText: selectedElement.childText,
            classNames:
              selectedElement.props.className
                ?.split(" ")
                .filter(Boolean)
                .join(" ") ?? "",
          });
        } else {
          set({
            selectedElement: null,
            editText: null,
            classNames: "",
          });
        }
      },
      setEditText: (editText: string | null) => set({ editText }),
      setClassNames: (classNames: string) => set({ classNames }),
      setDisplayMode: (displayMode: DisplayMode) => set({ displayMode }),
      setContextMenuOpen: (contextMenuOpen: boolean) =>
        set({ contextMenuOpen }),
      toggleDisplayMode: () => {
        const currentDisplayMode = get().displayMode;
        const newDisplayMode =
          currentDisplayMode === "interactive" ? "select" : "interactive";

        // NOTE (Kurt, 2025-07-16): Reset the store when toggling to interactive mode. This way we
        // have a fresh state when someone clicks on the select button again.
        if (newDisplayMode === "interactive") {
          get().actions.setSelectedElement(null);
          set({
            displayMode: newDisplayMode,
            contextMenuOpen: false,
          });
        } else {
          set({ displayMode: newDisplayMode });
        }
      },
      resetToDefaults: () =>
        set({
          selectedElement: null,
          editText: null,
          classNames: "",
          displayMode: "interactive",
          contextMenuOpen: false,
        }),
    },
  }));
};

type ModifiersContextType = {
  store: ReturnType<typeof createModifiersStore>;
};

const ModifiersContext = createContext<ModifiersContextType | null>(null);

const useModifiersContext = () => {
  return useRequiredContext(ModifiersContext);
};

interface ModifiersProviderProps {
  children: ReactNode;
}

export function ModifiersProvider({ children }: ModifiersProviderProps) {
  const [store] = React.useState(() => createModifiersStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ModifiersContext.Provider value={contextValue}>
      {children}
    </ModifiersContext.Provider>
  );
}

// NOTE (Kurt, 2025-07-16): Main hook to access the store
export function useModifiersStore<T = ModifiersStoreData>(
  selector?: (state: ModifiersStoreData) => T,
): T {
  const { store } = useModifiersContext();
  return useStore(store, selector as any);
}

// NOTE (Kurt, 2025-07-16): Convenience hooks for accessing specific values from the store.
// Define more as needed.
export const useSelectedElement = () =>
  useModifiersStore((state) => state.selectedElement);
